import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Settings2, ChevronDown, ChevronUp } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Subscription } from '@/hooks/useSubscription';

interface CancelSubscriptionSectionProps {
  subscription: Subscription;
  onCancelled: () => void;
  previewOnly?: boolean;
}

const REASONS = [
  { id: 'too_expensive', label: 'Too expensive for me right now' },
  { id: 'not_using', label: "I'm not using it enough" },
  { id: 'missing_features', label: 'Missing features I need' },
  { id: 'found_alternative', label: 'Found an alternative' },
  { id: 'temporary_break', label: 'Taking a temporary break' },
  { id: 'other', label: 'Other' },
];

export function CancelSubscriptionSection({ subscription, onCancelled, previewOnly = false }: CancelSubscriptionSectionProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<string>('');
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const cancelAtPeriodEnd = (subscription as any).cancel_at_period_end === true;
  const periodEndLabel = subscription.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString('en-IN', {
        day: 'numeric', month: 'long', year: 'numeric',
      })
    : 'the end of your billing period';

  const handleCancel = async () => {
    if (previewOnly) {
      toast({
        title: 'Preview mode',
        description: "This is an admin 'Viewing as' preview. Real members will hit their payment provider's cancellation here.",
      });
      setDialogOpen(false);
      return;
    }
    if (!reason) {
      toast({ title: 'Please pick a reason', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: { reason, feedback: feedback.trim() || undefined },
      });
      if (error) {
        let msg = error.message;
        try {
          const ctx = await (error as any)?.context?.json?.();
          if (ctx?.error) msg = ctx.error;
        } catch {}
        throw new Error(msg);
      }
      if (!data?.success) throw new Error(data?.error || 'Failed to cancel');
      toast({
        title: 'Membership cancelled',
        description: `You'll keep access until ${periodEndLabel}. No further charges.`,
      });
      setDialogOpen(false);
      onCancelled();
    } catch (err) {
      toast({
        title: 'Could not cancel',
        description: err instanceof Error ? err.message : 'Please try again or contact support.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (cancelAtPeriodEnd) {
    return (
      <Card className="rounded-none border-0 border-t border-[var(--sn-border)] bg-transparent px-0 py-6 shadow-none">
        <h4 className="font-medium text-foreground mb-1">Membership</h4>
        <p className="text-sm text-muted-foreground">
          Your membership is set to end on <span className="text-foreground font-medium">{periodEndLabel}</span>.
          You'll keep full access until then. No further charges will be made.
        </p>
      </Card>
    );
  }

  return (
    <Card className="rounded-none border-0 border-t border-[var(--sn-border)] bg-transparent px-0 py-6 shadow-none">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          <Settings2 className="w-4 h-4 text-muted-foreground" />
          <h4 className="font-medium text-foreground">Manage membership</h4>
          {previewOnly && (
            <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-lg bg-muted/40 text-muted-foreground">
              admin preview
            </span>
          )}
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </button>

      {open && (
        <div className="mt-4 space-y-3 text-sm">
          <p className="text-muted-foreground">
            If you cancel, you'll keep full Club access until{' '}
            <span className="text-foreground font-medium">{periodEndLabel}</span>, and your auto-renewal
            will be stopped with your payment provider. No further charges.
          </p>

          <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-muted-foreground hover:text-destructive hover:border-destructive/40">
                Cancel membership
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Cancel your Alpha with AI Club membership?</AlertDialogTitle>
                <AlertDialogDescription>
                  You'll keep full access until <strong>{periodEndLabel}</strong>. Your auto-renewal will be
                  stopped immediately with your payment provider. You can re-subscribe anytime.
                </AlertDialogDescription>
              </AlertDialogHeader>

              <div className="space-y-3 mt-2">
                <div>
                  <Label className="text-sm">Why are you cancelling? <span className="text-destructive">*</span></Label>
                  <div className="mt-2 space-y-1.5">
                    {REASONS.map((r) => (
                      <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="radio"
                          name="cancel-reason"
                          value={r.id}
                          checked={reason === r.id}
                          onChange={() => setReason(r.id)}
                          className="accent-primary"
                        />
                        <span className="text-foreground">{r.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="cancel-feedback" className="text-sm">
                    Anything we could have done better? <span className="text-muted-foreground">(optional)</span>
                  </Label>
                  <Textarea
                    id="cancel-feedback"
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value.slice(0, 1000))}
                    placeholder="Your feedback helps us improve."
                    rows={3}
                    className="mt-1"
                  />
                </div>
              </div>

              <AlertDialogFooter>
                <AlertDialogCancel disabled={submitting}>Keep membership</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => { e.preventDefault(); handleCancel(); }}
                  disabled={submitting || !reason}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {submitting ? (<><Loader2 className="w-4 h-4 animate-spin mr-2" /> Cancelling...</>) : 'Confirm cancellation'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </Card>
  );
}